import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { visibleSyncEntities } from '@cockpit/shared';
import { EmptyState, Spinner } from '@cockpit/ui';
import { Layout } from './components/Layout.js';
import { useMe } from './lib/queries.js';
import { AdminPage } from './pages/Admin.js';
import { CockpitPage } from './pages/Cockpit.js';
import { ConsolidePage } from './pages/Consolide.js';
import { DesignPage } from './pages/Design.js';
import { LoginPage } from './pages/Login.js';
import { NotFoundPage } from './pages/NotFound.js';
import { PlanPage } from './pages/PlanAction.js';
import { ProfilePage } from './pages/Profile.js';

function Protected({
  children,
  admin,
  allowPending,
}: {
  children: JSX.Element;
  admin?: boolean;
  allowPending?: boolean;
}) {
  const me = useMe();
  const loc = useLocation();
  if (me.isLoading) return <Spinner className="m-10" />;
  if (!me.data)
    return <Navigate to={`/connexion?retour=${encodeURIComponent(loc.pathname + loc.search)}`} replace />;
  if (admin && me.data.role !== 'admin') return <Navigate to="/" replace />;
  // Nouveau collaborateur connecté par Microsoft : rien n'est visible tant qu'un administrateur n'a pas attribué d'entité.
  const pending = !allowPending && visibleSyncEntities(me.data)?.length === 0;
  return (
    <Layout me={me.data}>
      {pending ? (
        <div className="mx-auto max-w-xl pt-10">
          <EmptyState title="Votre accès n’est pas encore configuré">
            Votre compte {me.data.email} est bien connecté, mais aucune entité ne vous a encore été attribuée.
            Demandez à un administrateur du cockpit de configurer votre périmètre, puis rechargez cette page.
          </EmptyState>
        </div>
      ) : (
        children
      )}
    </Layout>
  );
}

function Home() {
  const me = useMe();
  if (me.isLoading) return <Spinner className="m-10" />;
  return <Navigate to={me.data?.role === 'da' ? '/plan' : '/cockpit'} replace />;
}

export function App() {
  return (
    <Routes>
      <Route path="/connexion" element={<LoginPage />} />
      <Route path="/" element={<Home />} />
      <Route
        path="/cockpit"
        element={
          <Protected>
            <CockpitPage />
          </Protected>
        }
      />
      <Route
        path="/cockpit/consolide"
        element={
          <Protected>
            <ConsolidePage />
          </Protected>
        }
      />
      <Route
        path="/plan"
        element={
          <Protected>
            <PlanPage />
          </Protected>
        }
      />
      <Route
        path="/admin"
        element={
          <Protected admin>
            <AdminPage />
          </Protected>
        }
      />
      <Route
        path="/profil"
        element={
          <Protected allowPending>
            <ProfilePage />
          </Protected>
        }
      />
      <Route
        path="/design"
        element={
          <Protected>
            <DesignPage />
          </Protected>
        }
      />
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
}
